import { useCallback, useEffect, useState } from 'react';
import { useIntl } from 'react-intl';
import { useInventoryAppServices } from '@/app/services/InventoryAppServicesContext';
import { appSettingsRepository, defaultFieldAppSettings, type FieldAppSettings } from '@/app/settings/app-settings';
import type { FieldSyncRunResult } from '@/features/sync/engine/field-sync-runner';
import { createFieldSyncRunner } from '@/features/sync/engine/field-sync-runner';
import type { CaptureSession } from '@/shared/capture/capture-session';
import type { SyncQueueRecord } from '@/shared/db/schema';
import { SYNC_QUEUE_STATUSES } from '@/shared/db/schema';
import type { StorageHealth } from '@/shared/db/storage-health';
import { getStorageHealth } from '@/shared/db/storage-health';
import { messages } from './messages';

interface SyncProgressEntry extends FieldSyncRunResult {
  updatedAt: string;
}

interface SyncCenterState {
  currentSyncSessionId: string | null;
  error: string | null;
  isLoading: boolean;
  isSyncing: boolean;
  records: SyncQueueRecord[];
  settings: FieldAppSettings;
  sessions: CaptureSession[];
  syncProgress: SyncProgressEntry[];
  syncTotalCount: number;
  storageHealth: StorageHealth | null;
}

const emptyStorageHealth: StorageHealth = {
  draftCount: 0,
  photoBytes: 0,
  photoCount: 0,
  queuedSyncCount: 0,
  warningLevel: 'ok',
};

export function useSyncCenter(input: { locationId?: number | string } = {}) {
  const intl = useIntl();
  const { database, floorplanBlobs, photoBlobs, syncQueue: repository } = useInventoryAppServices();
  const [state, setState] = useState<SyncCenterState>({
    currentSyncSessionId: null,
    error: null,
    isLoading: true,
    isSyncing: false,
    records: [],
    settings: defaultFieldAppSettings,
    sessions: [],
    syncProgress: [],
    syncTotalCount: 0,
    storageHealth: null,
  });

  const readData = useCallback(async () => {
    if (typeof input.locationId === 'undefined') {
      const settings = await appSettingsRepository.load();

      return {
        records: [],
        settings,
        sessions: [],
        storageHealth: emptyStorageHealth,
      };
    }

    const [records, settings, sessions] = await Promise.all([
      repository.listAll(),
      appSettingsRepository.load(),
      database.getAll('capture_sessions'),
    ]);
    const visibleSessionIds = readLocationSessionIds({
      locationId: input.locationId,
      records,
      sessions,
    });
    await repository.recoverStaleSyncing(new Date().toISOString(), { sessionIds: visibleSessionIds });
    const [refreshedRecords, storageHealth] = await Promise.all([
      repository.listAll(),
      getStorageHealth(database, { locationId: input.locationId }),
    ]);

    return {
      records: refreshedRecords.filter(record => visibleSessionIds.has(record.sessionId)),
      settings,
      sessions: sessions.filter(session => visibleSessionIds.has(session.id)),
      storageHealth,
    };
  }, [database, input.locationId, repository]);

  const load = useCallback(async () => {
    setState(current => ({ ...current, error: null, isLoading: true }));

    try {
      const data = await readData();

      setState({
        currentSyncSessionId: null,
        error: null,
        isLoading: false,
        isSyncing: false,
        syncProgress: [],
        syncTotalCount: 0,
        ...data,
      });
    } catch {
      setState({
        currentSyncSessionId: null,
        error: intl.formatMessage(messages.syncErrorLoadSyncCenter),
        isLoading: false,
        isSyncing: false,
        records: [],
        settings: defaultFieldAppSettings,
        sessions: [],
        syncProgress: [],
        syncTotalCount: 0,
        storageHealth: null,
      });
    }
  }, [intl, readData]);

  function readVisibleSessionIds() {
    return new Set(state.records.map(record => record.sessionId));
  }

  async function retryAll() {
    await repository.retryAll(new Date().toISOString(), { sessionIds: readVisibleSessionIds() });
    await load();
  }

  async function retryOne(sessionId: string) {
    if (!readVisibleSessionIds().has(sessionId)) {
      return;
    }

    await repository.retry(sessionId, new Date().toISOString());
    await load();
  }

  async function runSync() {
    if (isSyncOffline()) {
      setState(current => ({
        ...current,
        error: intl.formatMessage(messages.syncOffline),
        isLoading: false,
      }));
      return;
    }

    const syncTotalCount = state.records.filter(isRunnableSyncRecord).length;
    setState(current => ({
      ...current,
      currentSyncSessionId: null,
      error: null,
      isLoading: false,
      isSyncing: true,
      syncProgress: [],
      syncTotalCount,
    }));

    try {
      const settings = await appSettingsRepository.load();
      const syncRunner = createFieldSyncRunner(settings, {
        database,
        floorplanBlobs,
        photoBlobs,
      });
      const sessionIds = readVisibleSessionIds();
      await repository.retryAll(new Date().toISOString(), { sessionIds });

      while (true) {
        const [nextRecord] = await repository.listPending({ sessionIds });

        if (!nextRecord) {
          break;
        }

        setState(current => ({
          ...current,
          currentSyncSessionId: nextRecord.sessionId,
        }));

        const result = await syncRunner.runNext(new Date().toISOString(), { sessionIds });

        if (result.status === 'idle') {
          break;
        }

        const updatedSession = result.sessionId ? await database.get('capture_sessions', result.sessionId) : undefined;
        setState(current => ({
          ...current,
          currentSyncSessionId: null,
          sessions: current.sessions.map(session =>
            updatedSession && session.id === updatedSession.id ? updatedSession : session,
          ),
          syncProgress: [...current.syncProgress, { ...result, updatedAt: new Date().toISOString() }],
        }));
      }

      const data = await readData();
      setState(current => ({
        ...current,
        ...data,
        currentSyncSessionId: null,
        isSyncing: false,
      }));
    } catch {
      setState(current => ({
        ...current,
        currentSyncSessionId: null,
        error: intl.formatMessage(messages.syncErrorRunSync),
        isLoading: false,
        isSyncing: false,
      }));
    }
  }

  useEffect(() => {
    void load();
  }, [load]);

  return {
    ...state,
    refresh: load,
    retryAll,
    retryOne,
    runSync,
  };
}

export function isSyncOffline(navigatorLike: { onLine?: boolean } | undefined = globalThis.navigator) {
  return navigatorLike?.onLine === false;
}

export function isRunnableSyncRecord(record: SyncQueueRecord) {
  return (
    record.status === SYNC_QUEUE_STATUSES.queued ||
    record.status === SYNC_QUEUE_STATUSES.syncFailed ||
    record.status === SYNC_QUEUE_STATUSES.needsReview ||
    record.status === SYNC_QUEUE_STATUSES.conflict
  );
}

function readLocationSessionIds(input: {
  locationId?: number | string;
  records: SyncQueueRecord[];
  sessions: CaptureSession[];
}) {
  if (typeof input.locationId === 'undefined') {
    return new Set<string>();
  }

  const locationId = String(input.locationId);
  const sessionIds = new Set(
    input.sessions.filter(session => String(session.locationId ?? '') === locationId).map(session => session.id),
  );

  for (const record of input.records) {
    if (String(record.locationId ?? '') === locationId) {
      sessionIds.add(record.sessionId);
    }
  }

  return sessionIds;
}
